import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { parse } from 'query-string';
import axios from 'axios';

import useClassnames from 'hook/use-classnames';
import cancelToken from 'component/core/cancel-token';
import history from 'component/core/history';

import UI from 'component/ui';
import Button from 'component/button';

import Form, { createRegistry } from 'component/form';
import { IPayload, IError } from 'component/form/types';
import FormInput from 'component/form/input';

import { IStore } from 'store/reducers/types/reducers';
import { key as keyUser } from 'store/reducers/user/reducer';
import { key as keyDeviceInfo } from 'store/reducers/deviceInfo/reducer';
import { set, reset } from 'store/reducers/user/actions';
import { addCart } from 'store/reducers/cart/actions';
import { login } from 'component/api/user';
import { ErrorCode } from 'component/api/types/api/account/login/post/code-400';

import style from './style.pcss';
import { useAlert } from 'component/alert/provider';

import useScript from 'hook/use-script';
import api from 'src/api';
import { CartItemsStore } from 'src/store/reducers/cart/types/reducer';
import { YandexTasksListData } from 'src/api/tasks/types';
import { resetTask, setTask } from 'src/store/reducers/task/actions';
import { CartItem } from 'src/api/cart/types';

const Login = () => {
    const cn = useClassnames(style);
    const { t } = useTranslation();
    const qs = useMemo(() => parse(history.location.search), [history.location.search]);
    const dispatch = useDispatch();
    const { show } = useAlert();
    const registry = useMemo(createRegistry, []);
    const cancelTokenLogin = useMemo(cancelToken.create, []);
    const cancelTokenUserSelfInfo = useMemo(cancelToken.create, []);
    const cancelTokenSendVerifiEmail = useMemo(cancelToken.create, []);

    const isAuth = useSelector<IStore, boolean>((store) => !!store[keyUser].id);
    const isMobile = useSelector<IStore, boolean>((state) => state[keyDeviceInfo].mobile);

    const [pending, setPending] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
    const [errors, setErrors] = useState<Array<IError> | null>(null);

    const [cartItemsListPage, setCartItemsListPage] = useState<number>(1);
    const [isCartItemsListLoading, setIsCartItemsListLoading] = useState<boolean>(false);
    const [isCartItemsListLoadMore, setIsCartItemsListLoadMore] = useState<boolean>(false);
    const [cartItemsList, setCartItemsList] = useState<Array<CartItem>>([]);
    const [isLoadingPersonInfo, setIsLoadingPersonInfo] = useState<boolean>(false);

    useEffect(() => {
        if (localStorage.getItem('jwt_token') && isLoadingPersonInfo) {
            setTimeout(async () => {
                const payload = await api.accounts.getAccountRetrieve();
                dispatch(set(payload.data));
            }, 300);
            setPending(false);
            setIsLoadingPersonInfo(false);
        }
    }, [isLoadingPersonInfo]);

    useEffect(() => {
        if (isCartItemsListLoading || isCartItemsListLoadMore) {
            api.cart.getCartItemsList({
                pageNumber: cartItemsListPage
            })
            .then((resp) => {
                const filteredList: CartItemsStore = {
                    count: 0,
                    items: []
                };

                setCartItemsList((prev) => isCartItemsListLoadMore ?
                    [...prev, ...resp.data.results] : resp.data.results);
                if (!resp.data.next) {
                    const newList = isCartItemsListLoadMore ? [...cartItemsList, ...resp.data.results].map((item) => filteredList.items.push(item))
                        : resp.data.results.map((item) => filteredList.items.push(item));
                    filteredList.count = resp.data.count;
                    dispatch(addCart(filteredList));
                    setIsCartItemsListLoading(false);
                    setIsCartItemsListLoadMore(false);
                } else {
                    setIsCartItemsListLoading(false);
                    setIsCartItemsListLoadMore(false);

                    setCartItemsListPage((prev) => prev + 1);
                    setIsCartItemsListLoadMore(true);
                }
            });
        }
    }, [isCartItemsListLoading, isCartItemsListLoadMore]);

    useEffect(() => {
        const promise = useScript('https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js');

        promise.then(
        () => {
            authYa();
        },
        // tslint:disable-next-line: no-shadowed-variable
        (error: { message: string; }) => {
            alert(`Ошибка: ${error.message}`);
        }
        );

        return () => {
            cancelTokenLogin.remove();
            cancelTokenUserSelfInfo.remove();
            cancelTokenSendVerifiEmail.remove();
        };
    }, []);

    const getUserInfo = async (access_token: string) => {
        if(!pending) {
            setPending(true);

            api.auth.authYandexCreate(access_token)
                .then((resp) => {
                    localStorage.setItem('jwt_token', resp.data.access_token);
                    localStorage.setItem('jwt_token_refresh', resp.data.refresh_token);

                    setCartItemsListPage(1);
                    setIsCartItemsListLoading(true);

                    const taskFilter: YandexTasksListData = {
                        seen: false
                    };

                    api.tasks.getYandexTasksList(undefined, taskFilter)
                        .then((payload) => {
                            dispatch(setTask(payload.data.count));
                        })
                        .catch(() => {
                            dispatch(resetTask());
                        });

                    setIsLoadingPersonInfo(true);
                })
                .catch((payload) => {
                    if(!axios.isCancel(payload)) {
                        setError(payload.message);
                        setErrorCode(payload.error_code);
                        setPending(false);

                        show(payload.message, 'danger');

                        if(payload.errors) {
                            setErrors(payload.errors);
                        }

                        dispatch(reset());
                    }
                });
    }
    };

    const authYa = useCallback(() => {
        // @ts-ignore
        YaAuthSuggest.init({
            client_id: process.env.YANDEX_API_CLIENT_ID,
            response_type: 'token',
            redirect_uri: `${location.origin}/auth-ya/suggest/token`
            },
            `${location.origin}`, {
                view: 'button',
                parentId: 'auth-container',
                buttonView: 'main',
                buttonTheme: 'light',
                buttonSize: 'm',
                buttonBorderRadius: 4
             }
         )
            .then((result: {handler: Function}) => {
                return result.handler();
            })
            .then((data: {'access_token': string; 'token_type': string; 'expires_in': string; 'extra_data': {}}) => {
                getUserInfo(data.access_token);
            })
            // tslint:disable-next-line: no-shadowed-variable
            .catch((error: unknown) => {
                alert(`Что-то пошло не так: ${JSON.stringify(error)}`);
            });
    }, [pending]);

    const onSubmit = useCallback((formData: IPayload) => {
        if(isFormValid && !pending) {
            setPending(true);

            if(errorCode) {
                setErrorCode(null);
            }

            if(error) {
                setError(null);
            }

            if(errors) {
                setErrors(null);
            }

            api.auth.authTokenCreate(
                formData.email,
                formData.password
            )
                .then((resp) => {
                    localStorage.setItem('jwt_token', resp.data.access);
                    localStorage.setItem('jwt_token_refresh', resp.data.refresh);

                    setCartItemsListPage(1);
                    setIsCartItemsListLoading(true);

                    const taskFilter: YandexTasksListData = {
                        seen: false
                    };

                    api.tasks.getYandexTasksList(undefined, taskFilter)
                        .then((payload) => {
                            dispatch(setTask(payload.data.count));
                        })
                        .catch(() => {
                            dispatch(resetTask());
                            });

                    setIsLoadingPersonInfo(true);
                })
                .catch((payload) => {
                    if(!axios.isCancel(payload)) {
                        setError(payload.message);
                        setErrorCode(payload.error_code);
                        setPending(false);

                        show(payload.message, 'danger');

                        if(payload.errors) {
                            setErrors(payload.errors);
                        }

                        dispatch(reset());
                    }
                });
        }
    }, [isFormValid, pending]);

    const onChangeValidState = useCallback((payload: boolean) => {
        if(isFormValid !== payload) {
            setIsFormValid(payload);
        }
    }, [isFormValid]);

    if(isAuth) {
        if(typeof qs.from === 'string') {
            return <Redirect to={qs.from} />;
        } else if(typeof qs['from-external'] === 'string') {
            location.href = location.origin + qs['from-external'];
        } else if(history.location.state && history.location.state.from) {
            return <Redirect to={history.location.state.from} />;
        } else {
            return <Redirect to="/subscriptions" />;
        }
    }

    return (
        <UI.Main className={cn('login__ui-main')}>
            <Helmet
                title={t('helmet.title.login')}
                meta={[{
                    name   : 'document-state',
                    content: 'static'
                }]}
            />

            <UI.Box padding={true} className={cn('login__ui-box')}>
                <div className={cn('login__content')}>
                    <h2 className={cn('login__header')}>
                        {t('route.login.header')}
                        <Link
                            to="/registration"
                            className={cn('login__link-registration')}
                        >
                            {t('route.login.link.registration')}
                        </Link>
                    </h2>
                    <Form
                        registry={registry.form}
                        className={cn('login__form')}
                        onSubmit={onSubmit}
                        onChangeValidity={onChangeValidState}
                        errors={errors}
                    >
                        <FormInput
                            registry={registry.field}
                            name="email"
                            type="email"
                            className={cn('login__input')}
                            direction="column"
                            required={true}
                            autoFocus={true}
                            tabIndex={1}
                        >
                            {t('route.login.input.email.label')}
                        </FormInput>
                        <FormInput
                            registry={registry.field}
                            name="password"
                            type="password"
                            className={cn('login__input')}
                            direction="column"
                            required={true}
                            tabIndex={2}
                        >
                            {t('route.login.input.password.label')}
                        </FormInput>
                        <Link
                            to={{
                                pathname: '/password/reset',
                                state   : {
                                    modal: !isMobile
                                }
                            }}
                            className={cn('login__link-reset-password')}
                        >
                            {t('route.login.link.reset-password')}
                        </Link>
                        <Button
                            type="submit"
                            className={cn('login__button-submit')}
                            isLoading={pending}
                            disabled={!isFormValid || pending}
                        >
                            Войти
                        </Button>
                    </Form>
                    <div id="auth-container" className={cn('login__auth-container')} />
                </div>
            </UI.Box>
        </UI.Main>
    );
};

export default Login;
